import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useRoute } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  ArrowRight,
  ClipboardList,
  Plus,
  X,
  Mic,
  Square,
  Camera,
  Trash2,
  Loader2,
  FileText,
  Send,
  Pencil,
  Check,
  Home,
  ChefHat,
  Bath,
  BedDouble,
  Sofa,
  DoorOpen,
  Trees,
  Car,
  Warehouse,
  Building2,
  Wrench,
  Flame,
  HardHat,
  Images,
} from "lucide-react";
import { useUpload } from "@/hooks/use-upload";
import { generateSiteSurveyPdf } from "@/lib/site-survey-pdf";
import { usePageTour } from "@/components/ProductTour";

// ============================================================================
// Types
// ============================================================================

interface VoiceNote {
  id: string;
  transcript: string;
  audioObjectPath?: string;
  durationSec?: number;
  createdAt: string;
}

interface PhotoEntry {
  id: string;
  objectPath: string;
  caption?: string;
  createdAt: string;
}

interface RoomEntry {
  id: string;
  name: string;
  templateId?: string;
  voiceNotes?: VoiceNote[];
  typedNotes?: string;
  workItems?: string[];
  photos?: PhotoEntry[];
}

interface SurveyData {
  project?: {
    clientName?: string;
    projectAddress?: string;
    projectType?: string;
    description?: string;
    capturedBy?: string;
    visitDate?: string;
  };
  rooms?: RoomEntry[];
  generalNotes?: string;
}

interface SiteSurvey {
  id: string;
  userId: string;
  clientId: string | null;
  quoteId: string | null;
  title: string;
  status: "draft" | "completed";
  projectAddress: string | null;
  projectType: string | null;
  reportObjectPath: string | null;
  data: SurveyData;
  createdAt: string;
  updatedAt: string;
}

type Step = "setup" | "rooms" | "capture" | "review";

// ============================================================================
// Room templates (UK-first, with Conservatory/Outhouse/Soffit/Chimney/Boiler)
// ============================================================================

const ROOM_TEMPLATES: Array<{ id: string; name: string; icon: any }> = [
  { id: "kitchen", name: "Kitchen", icon: ChefHat },
  { id: "bathroom", name: "Bathroom", icon: Bath },
  { id: "ensuite", name: "En-suite", icon: Bath },
  { id: "wc", name: "WC / Cloakroom", icon: DoorOpen },
  { id: "utility", name: "Utility", icon: Wrench },
  { id: "lounge", name: "Lounge", icon: Sofa },
  { id: "dining", name: "Dining Room", icon: Sofa },
  { id: "snug", name: "Snug", icon: Sofa },
  { id: "open-plan", name: "Open-Plan Area", icon: Sofa },
  { id: "bedroom", name: "Bedroom", icon: BedDouble },
  { id: "master", name: "Master Bedroom", icon: BedDouble },
  { id: "study", name: "Study / Office", icon: BedDouble },
  { id: "hallway", name: "Hallway", icon: DoorOpen },
  { id: "landing", name: "Landing", icon: DoorOpen },
  { id: "stairs", name: "Stairs", icon: DoorOpen },
  { id: "loft", name: "Loft", icon: Home },
  { id: "garage", name: "Garage", icon: Car },
  { id: "outhouse", name: "Outhouse", icon: Warehouse },
  { id: "conservatory", name: "Conservatory", icon: Trees },
  { id: "extension", name: "Extension Area", icon: Building2 },
  { id: "garden", name: "Garden / Exterior", icon: Trees },
  { id: "roof", name: "Roof", icon: Home },
  { id: "soffit", name: "Soffits & Fascias", icon: Home },
  { id: "chimney", name: "Chimney", icon: Flame },
  { id: "boiler", name: "Boiler / Plant Cupboard", icon: Wrench },
  { id: "facade", name: "External Facade", icon: Building2 },
  { id: "site", name: "Site Access / Welfare", icon: HardHat },
  { id: "custom", name: "Custom Area", icon: Plus },
];

const PROJECT_TYPES = [
  { value: "extension", label: "Extension" },
  { value: "loft", label: "Loft Conversion" },
  { value: "renovation", label: "Renovation / Refurbishment" },
  { value: "new-build", label: "New Build" },
  { value: "kitchen", label: "Kitchen Refit" },
  { value: "bathroom", label: "Bathroom Refit" },
  { value: "garage-conversion", label: "Garage Conversion" },
  { value: "outbuilding", label: "Outbuilding / Annexe" },
  { value: "external", label: "External Works" },
  { value: "structural", label: "Structural Works" },
  { value: "maintenance", label: "Maintenance / Repairs" },
  { value: "other", label: "Other" },
];

function genId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

// ============================================================================
// Page
// ============================================================================

export default function QuoteSurveyPage() {
  usePageTour("quote-survey");
  const [, navigate] = useLocation();
  const [, params] = useRoute("/quotes/survey/:id");
  const surveyId = params?.id;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ── Survey load ─────────────────────────────────────────────────────────
  const { data: survey, isLoading: loadingSurvey } = useQuery<SiteSurvey>({
    queryKey: ["/api/site-surveys", surveyId],
    enabled: !!surveyId,
  });

  // ── Local working state (mirrors `survey.data`, autosaved) ──────────────
  const [step, setStep] = useState<Step>("setup");
  const [data, setData] = useState<SurveyData>({});
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);

  // Hydrate from server when survey loads
  useEffect(() => {
    if (survey) {
      setData(survey.data || {});
      // If we land on an existing draft with rooms already, jump to review
      const roomCount = survey.data?.rooms?.length ?? 0;
      if (roomCount > 0) setStep("review");
      else if (survey.data?.project) setStep("rooms");
    }
  }, [survey?.id]);

  // ── Autosave (debounced, sequenced, success-tracked) ────────────────────
  // We only mark `lastSavedRef` after the PATCH actually returns 200, so a
  // failed save is retried on the next edit. Saves are sequenced via a
  // promise chain so a slow earlier PATCH cannot overwrite a newer one. A
  // `flushPendingSave` helper is exposed so the Review screen can guarantee
  // all autosaves are durable before "Send to Auto Quote" navigates away.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<string>("");
  const pendingDataRef = useRef<SurveyData | null>(null);
  const inflightRef = useRef<Promise<unknown>>(Promise.resolve());

  const runSave = useCallback(
    (next: SurveyData) => {
      if (!surveyId) return Promise.resolve();
      const serialized = JSON.stringify(next);
      if (serialized === lastSavedRef.current) return Promise.resolve();
      const payload = {
        data: next,
        title:
          next.project?.projectAddress?.trim() ||
          next.project?.clientName?.trim() ||
          "Untitled site visit",
        projectAddress: next.project?.projectAddress || null,
        projectType: next.project?.projectType || null,
      };
      // Chain onto the inflight promise so writes always serialize.
      const chained = inflightRef.current.then(async () => {
        const res = await fetch(`/api/site-surveys/${surveyId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to save survey");
        // Only mark saved on success — failed saves stay dirty and retry.
        lastSavedRef.current = serialized;
        queryClient.invalidateQueries({ queryKey: ["/api/site-surveys"] });
        return res.json();
      });
      inflightRef.current = chained.catch(() => undefined);
      return chained;
    },
    [surveyId, queryClient]
  );

  const scheduleSave = useCallback(
    (next: SurveyData) => {
      if (!surveyId) return;
      pendingDataRef.current = next;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        saveTimer.current = null;
        const data = pendingDataRef.current;
        pendingDataRef.current = null;
        if (data) runSave(data).catch(() => undefined);
      }, 600);
    },
    [surveyId, runSave]
  );

  // Flush any pending debounce immediately, then await all inflight writes.
  // Used before navigating away (Send to Auto Quote, completion, etc).
  const flushPendingSave = useCallback(async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    const data = pendingDataRef.current;
    pendingDataRef.current = null;
    if (data) await runSave(data);
    // Wait on the chain in case other saves are still in flight.
    await inflightRef.current;
  }, [runSave]);

  function patchData(patch: (prev: SurveyData) => SurveyData) {
    setData((prev) => {
      const next = patch(prev);
      scheduleSave(next);
      return next;
    });
  }

  // ── Create survey ───────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: async (initial: { project: SurveyData["project"] }) => {
      const res = await fetch(`/api/site-surveys`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title:
            initial.project?.projectAddress?.trim() ||
            initial.project?.clientName?.trim() ||
            "Untitled site visit",
          projectAddress: initial.project?.projectAddress || null,
          projectType: initial.project?.projectType || null,
          data: { project: initial.project, rooms: [] },
        }),
      });
      if (!res.ok) throw new Error("Failed to create survey");
      return res.json() as Promise<SiteSurvey>;
    },
    onSuccess: (created) => {
      navigate(`/quotes/survey/${created.id}`, { replace: true });
      queryClient.invalidateQueries({ queryKey: ["/api/site-surveys"] });
    },
    onError: (err: any) => {
      toast({ title: "Could not start survey", description: err.message, variant: "destructive" });
    },
  });

  // ── Step renderers ──────────────────────────────────────────────────────
  if (surveyId && loadingSurvey) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      <Header step={step} onBack={() => navigate("/site-surveys")} />

      {step === "setup" && (
        <SetupStep
          initial={data.project}
          onStart={async (project) => {
            const next = { ...data, project, rooms: data.rooms || [] };
            setData(next);
            if (!surveyId) {
              await createMutation.mutateAsync({ project });
              setStep("rooms");
            } else {
              scheduleSave(next);
              setStep("rooms");
            }
          }}
          isCreating={createMutation.isPending}
        />
      )}

      {step === "rooms" && (
        <RoomsStep
          rooms={data.rooms || []}
          onAddRoom={(template) => {
            const existingCount = (data.rooms || []).filter((r) => r.templateId === template.id).length;
            const name =
              template.id === "custom"
                ? "Custom Area"
                : existingCount > 0
                ? `${template.name} ${existingCount + 1}`
                : template.name;
            const newRoom: RoomEntry = {
              id: genId("room"),
              name,
              templateId: template.id,
            };
            patchData((prev) => ({ ...prev, rooms: [...(prev.rooms || []), newRoom] }));
            setActiveRoomId(newRoom.id);
            setStep("capture");
          }}
          onRemoveRoom={(roomId) => {
            patchData((prev) => ({
              ...prev,
              rooms: (prev.rooms || []).filter((r) => r.id !== roomId),
            }));
          }}
          onOpenRoom={(roomId) => {
            setActiveRoomId(roomId);
            setStep("capture");
          }}
          onBack={() => setStep("setup")}
          onContinue={() => setStep("review")}
        />
      )}

      {step === "capture" && activeRoomId && (
        <CaptureStep
          room={(data.rooms || []).find((r) => r.id === activeRoomId)!}
          onUpdate={(updated) =>
            patchData((prev) => ({
              ...prev,
              rooms: (prev.rooms || []).map((r) => (r.id === updated.id ? updated : r)),
            }))
          }
          onBack={() => {
            setActiveRoomId(null);
            setStep("rooms");
          }}
          onDone={() => {
            setActiveRoomId(null);
            setStep("rooms");
          }}
        />
      )}

      {step === "review" && (
        <ReviewStep
          surveyId={surveyId!}
          data={data}
          onEditProject={() => setStep("setup")}
          onEditRoom={(roomId) => {
            setActiveRoomId(roomId);
            setStep("capture");
          }}
          onAddMoreRooms={() => setStep("rooms")}
          onUpdateGeneralNotes={(notes) =>
            patchData((prev) => ({ ...prev, generalNotes: notes }))
          }
          flushPendingSave={flushPendingSave}
        />
      )}
    </div>
  );
}

// ============================================================================
// Header
// ============================================================================

function Header({ step, onBack }: { step: Step; onBack: () => void }) {
  return (
    <div className="mb-6">
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-3 -ml-2" data-testid="button-back-to-surveys">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to surveys
      </Button>
      <div className="flex items-center gap-3">
        <div className="rounded-md bg-primary/10 p-2 text-primary">
          <ClipboardList className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-xl font-semibold">Site Survey</h1>
          <p className="text-sm text-muted-foreground">
            Walk the property capturing voice notes, photos and work items per room. Generates a Site Visit Report.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-4 text-xs">
        {(["setup", "rooms", "capture", "review"] as Step[]).map((s, i) => {
          const active = s === step;
          return (
            <div key={s} className="flex items-center gap-2">
              <span
                className={
                  active
                    ? "rounded-full bg-primary text-primary-foreground px-2.5 py-0.5 font-medium"
                    : "rounded-full bg-muted text-muted-foreground px-2.5 py-0.5"
                }
              >
                {i + 1}. {s === "setup" ? "Project" : s === "rooms" ? "Rooms" : s === "capture" ? "Capture" : "Review"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Step 1: Project setup
// ============================================================================

function SetupStep({
  initial,
  onStart,
  isCreating,
}: {
  initial?: SurveyData["project"];
  onStart: (project: SurveyData["project"]) => Promise<void> | void;
  isCreating: boolean;
}) {
  const [clientName, setClientName] = useState(initial?.clientName || "");
  const [projectAddress, setProjectAddress] = useState(initial?.projectAddress || "");
  const [projectType, setProjectType] = useState(initial?.projectType || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [capturedBy, setCapturedBy] = useState(initial?.capturedBy || "");
  const [visitDate, setVisitDate] = useState(
    initial?.visitDate || new Date().toISOString().slice(0, 10)
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Project details</CardTitle>
        <p className="text-xs text-muted-foreground">All fields optional. You can change these any time.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="client">Client name</Label>
            <Input
              id="client"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="e.g. Mr & Mrs Smith"
              data-testid="input-survey-client-name"
            />
          </div>
          <div>
            <Label htmlFor="address">Site address</Label>
            <Input
              id="address"
              value={projectAddress}
              onChange={(e) => setProjectAddress(e.target.value)}
              placeholder="e.g. 14 Acacia Avenue, London"
              data-testid="input-survey-address"
            />
          </div>
          <div>
            <Label htmlFor="type">Project type</Label>
            <Select value={projectType} onValueChange={setProjectType}>
              <SelectTrigger id="type" data-testid="select-survey-project-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="date">Visit date</Label>
            <Input id="date" type="date" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} data-testid="input-survey-date" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="captured">Captured by</Label>
            <Input
              id="captured"
              value={capturedBy}
              onChange={(e) => setCapturedBy(e.target.value)}
              placeholder="Your name"
              data-testid="input-survey-captured-by"
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="desc">Project description</Label>
            <Textarea
              id="desc"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="One or two lines describing the project overall."
              data-testid="textarea-survey-description"
            />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={() =>
              onStart({
                clientName: clientName || undefined,
                projectAddress: projectAddress || undefined,
                projectType: projectType || undefined,
                description: description || undefined,
                capturedBy: capturedBy || undefined,
                visitDate: visitDate || undefined,
              })
            }
            disabled={isCreating}
            data-testid="button-survey-start"
          >
            {isCreating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Start walkthrough <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Step 2: Rooms grid
// ============================================================================

function RoomsStep({
  rooms,
  onAddRoom,
  onRemoveRoom,
  onOpenRoom,
  onBack,
  onContinue,
}: {
  rooms: RoomEntry[];
  onAddRoom: (template: { id: string; name: string }) => void;
  onRemoveRoom: (roomId: string) => void;
  onOpenRoom: (roomId: string) => void;
  onBack: () => void;
  onContinue: () => void;
}) {
  return (
    <div className="space-y-6">
      {rooms.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Rooms in this survey ({rooms.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {rooms.map((r) => {
              const summary = roomSummary(r);
              return (
                <div
                  key={r.id}
                  className="flex items-center gap-3 p-3 rounded-md border bg-card hover:bg-accent/50 transition-colors"
                >
                  <button
                    onClick={() => onOpenRoom(r.id)}
                    className="flex-1 text-left"
                    data-testid={`button-open-room-${r.id}`}
                  >
                    <div className="font-medium text-sm">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{summary}</div>
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemoveRoom(r.id)}
                    data-testid={`button-remove-room-${r.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add a room or area</CardTitle>
          <p className="text-xs text-muted-foreground">Tap a template to start capturing.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {ROOM_TEMPLATES.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onAddRoom(t)}
                  className="flex flex-col items-center justify-center gap-2 p-3 rounded-md border bg-card hover:border-primary/50 hover:bg-primary/5 transition-colors text-center"
                  data-testid={`button-template-${t.id}`}
                >
                  <Icon className="h-5 w-5 text-primary" />
                  <span className="text-xs font-medium">{t.name}</span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack} data-testid="button-rooms-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Button onClick={onContinue} disabled={rooms.length === 0} data-testid="button-rooms-continue">
          Review survey <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function roomSummary(r: RoomEntry): string {
  const bits: string[] = [];
  const v = r.voiceNotes?.length || 0;
  const p = r.photos?.length || 0;
  const w = r.workItems?.length || 0;
  if (v) bits.push(`${v} voice note${v === 1 ? "" : "s"}`);
  if (p) bits.push(`${p} photo${p === 1 ? "" : "s"}`);
  if (w) bits.push(`${w} work item${w === 1 ? "" : "s"}`);
  if (r.typedNotes?.trim()) bits.push("notes");
  return bits.length ? bits.join(" · ") : "No content yet";
}

// ============================================================================
// Step 3: Capture (per room)
// ============================================================================

function CaptureStep({
  room,
  onUpdate,
  onBack,
  onDone,
}: {
  room: RoomEntry;
  onUpdate: (room: RoomEntry) => void;
  onBack: () => void;
  onDone: () => void;
}) {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(room.name);
  const [workInput, setWorkInput] = useState("");

  // Keep local name in sync if room changes externally
  useEffect(() => setName(room.name), [room.id]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            {editingName ? (
              <>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-8 text-base"
                  data-testid="input-room-name"
                />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => {
                    onUpdate({ ...room, name: name.trim() || room.name });
                    setEditingName(false);
                  }}
                  data-testid="button-room-name-save"
                >
                  <Check className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <CardTitle className="text-base flex-1">{room.name}</CardTitle>
                <Button size="icon" variant="ghost" onClick={() => setEditingName(true)} data-testid="button-room-name-edit">
                  <Pencil className="h-4 w-4 text-muted-foreground" />
                </Button>
              </>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Capture everything you see and hear here. Autosaves.</p>
        </CardHeader>
      </Card>

      {/* Voice */}
      <VoiceRecorderCard
        notes={room.voiceNotes || []}
        onAdd={(note) => onUpdate({ ...room, voiceNotes: [...(room.voiceNotes || []), note] })}
        onRemove={(id) =>
          onUpdate({ ...room, voiceNotes: (room.voiceNotes || []).filter((n) => n.id !== id) })
        }
        onUpdate={(id, transcript) =>
          onUpdate({
            ...room,
            voiceNotes: (room.voiceNotes || []).map((n) => (n.id === id ? { ...n, transcript } : n)),
          })
        }
      />

      {/* Photos */}
      <PhotoCard
        photos={room.photos || []}
        onAdd={(photo) => onUpdate({ ...room, photos: [...(room.photos || []), photo] })}
        onRemove={(id) =>
          onUpdate({ ...room, photos: (room.photos || []).filter((p) => p.id !== id) })
        }
        onCaption={(id, caption) =>
          onUpdate({ ...room, photos: (room.photos || []).map((p) => (p.id === id ? { ...p, caption } : p)) })
        }
      />

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Typed notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            rows={4}
            value={room.typedNotes || ""}
            onChange={(e) => onUpdate({ ...room, typedNotes: e.target.value })}
            placeholder="Anything you don't want to say out loud — measurements, quirks, access notes."
            data-testid="textarea-room-notes"
          />
        </CardContent>
      </Card>

      {/* Work items */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Work items</CardTitle>
          <p className="text-xs text-muted-foreground">Quick chips for items you spotted that will need pricing.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              value={workInput}
              onChange={(e) => setWorkInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && workInput.trim()) {
                  e.preventDefault();
                  onUpdate({ ...room, workItems: [...(room.workItems || []), workInput.trim()] });
                  setWorkInput("");
                }
              }}
              placeholder='e.g. "Strip out kitchen", "New steel beam over rear opening"'
              data-testid="input-work-item"
            />
            <Button
              type="button"
              onClick={() => {
                if (!workInput.trim()) return;
                onUpdate({ ...room, workItems: [...(room.workItems || []), workInput.trim()] });
                setWorkInput("");
              }}
              data-testid="button-add-work-item"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {room.workItems && room.workItems.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {room.workItems.map((w, i) => (
                <Badge key={i} variant="secondary" className="gap-1.5 py-1 pl-2.5 pr-1">
                  {w}
                  <button
                    onClick={() =>
                      onUpdate({
                        ...room,
                        workItems: (room.workItems || []).filter((_, idx) => idx !== i),
                      })
                    }
                    className="hover:bg-muted-foreground/20 rounded-sm p-0.5"
                    data-testid={`button-remove-work-item-${i}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between sticky bottom-2 z-10">
        <Button variant="outline" onClick={onBack} data-testid="button-capture-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Rooms
        </Button>
        <Button onClick={onDone} data-testid="button-capture-done">
          Done with this room <Check className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Voice Recorder
// ============================================================================

function VoiceRecorderCard({
  notes,
  onAdd,
  onRemove,
  onUpdate,
}: {
  notes: VoiceNote[];
  onAdd: (note: VoiceNote) => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, transcript: string) => void;
}) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { toast } = useToast();

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick a mime the browser supports
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
      const mimeType = candidates.find((c) => (window as any).MediaRecorder?.isTypeSupported?.(c)) || "";
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        const duration = (Date.now() - startTimeRef.current) / 1000;
        await transcribeBlob(blob, duration);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      startTimeRef.current = Date.now();
      setElapsed(0);
      setRecording(true);
      tickRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 250);
    } catch (err: any) {
      toast({
        title: "Microphone unavailable",
        description: err?.message || "Please allow microphone access in your browser.",
        variant: "destructive",
      });
    }
  }

  function stopRecording() {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    setRecording(false);
  }

  async function transcribeBlob(blob: Blob, durationSec: number) {
    setTranscribing(true);
    try {
      // Encode via FileReader.readAsDataURL — `String.fromCharCode(...bytes)`
      // blows the call stack for recordings of more than a few seconds.
      const base64: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const idx = result.indexOf(",");
          resolve(idx >= 0 ? result.slice(idx + 1) : result);
        };
        reader.onerror = () => reject(reader.error || new Error("read failed"));
        reader.readAsDataURL(blob);
      });
      const res = await fetch("/api/audio/transcribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ audio: base64 }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Transcription failed");
      }
      const { text } = await res.json();
      onAdd({
        id: genId("voice"),
        transcript: text || "",
        durationSec: Math.round(durationSec),
        createdAt: new Date().toISOString(),
      });
    } catch (err: any) {
      toast({ title: "Transcription failed", description: err.message, variant: "destructive" });
    } finally {
      setTranscribing(false);
    }
  }

  useEffect(() => {
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        try {
          mediaRecorderRef.current.stop();
        } catch {}
      }
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Mic className="h-4 w-4" /> Voice notes
        </CardTitle>
        <p className="text-xs text-muted-foreground">Tap record, walk and talk. Auto-transcribed when you stop.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-3">
          {!recording ? (
            <Button
              type="button"
              onClick={startRecording}
              disabled={transcribing}
              data-testid="button-voice-start"
            >
              <Mic className="h-4 w-4 mr-2" /> Record
            </Button>
          ) : (
            <Button
              type="button"
              onClick={stopRecording}
              variant="destructive"
              data-testid="button-voice-stop"
            >
              <Square className="h-4 w-4 mr-2" /> Stop ({formatTime(elapsed)})
            </Button>
          )}
          {transcribing && (
            <span className="text-xs text-muted-foreground inline-flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Transcribing…
            </span>
          )}
        </div>

        {notes.length > 0 && (
          <div className="space-y-2">
            {notes.map((n, idx) => (
              <div key={n.id} className="rounded-md border p-3 bg-muted/30">
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="text-[10px]">
                    Note {idx + 1}
                    {n.durationSec ? ` · ${formatTime(n.durationSec)}` : ""}
                  </Badge>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="ml-auto h-6 w-6"
                    onClick={() => onRemove(n.id)}
                    data-testid={`button-voice-remove-${n.id}`}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <Textarea
                  value={n.transcript}
                  onChange={(e) => onUpdate(n.id, e.target.value)}
                  rows={3}
                  className="mt-2 text-sm"
                  data-testid={`textarea-voice-${n.id}`}
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// ============================================================================
// Photo capture
// ============================================================================

function PhotoCard({
  photos,
  onAdd,
  onRemove,
  onCaption,
}: {
  photos: PhotoEntry[];
  onAdd: (photo: PhotoEntry) => void;
  onRemove: (id: string) => void;
  onCaption: (id: string, caption: string) => void;
}) {
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const libraryRef = useRef<HTMLInputElement | null>(null);
  const { uploadFile, isUploading } = useUpload();
  const { toast } = useToast();
  const [pendingCount, setPendingCount] = useState(0);

  async function handleFiles(files: FileList | null, inputRef: React.RefObject<HTMLInputElement | null>) {
    if (!files || files.length === 0) return;
    setPendingCount(files.length);
    try {
      for (const file of Array.from(files)) {
        const result = await uploadFile(file);
        if (result) {
          onAdd({
            id: genId("photo"),
            objectPath: result.objectPath,
            createdAt: new Date().toISOString(),
          });
        } else {
          toast({ title: "Upload failed", description: file.name, variant: "destructive" });
        }
      }
    } finally {
      setPendingCount(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Camera className="h-4 w-4" /> Photos
        </CardTitle>
        <p className="text-xs text-muted-foreground">Take a photo on site or pick from your library. Multiple at a time.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Camera input — opens camera directly on mobile */}
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files, cameraRef)}
          data-testid="input-photo-camera"
        />
        {/* Library input — opens photo library on mobile */}
        <input
          ref={libraryRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files, libraryRef)}
          data-testid="input-photo-library"
        />
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => cameraRef.current?.click()}
            disabled={isUploading}
            data-testid="button-take-photo"
          >
            {isUploading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Camera className="h-4 w-4 mr-2" />
            )}
            Take Photo
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => libraryRef.current?.click()}
            disabled={isUploading}
            data-testid="button-photo-library"
          >
            {isUploading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Images className="h-4 w-4 mr-2" />
            )}
            Photo Library
          </Button>
        </div>
        {isUploading && pendingCount > 0 && (
          <p className="text-xs text-muted-foreground">Uploading {pendingCount} photo{pendingCount > 1 ? "s" : ""}…</p>
        )}

        {photos.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map((p) => (
              <div key={p.id} className="relative group rounded-md overflow-hidden border bg-muted">
                <img
                  src={p.objectPath}
                  alt={p.caption || "Site photo"}
                  className="w-full aspect-square object-cover"
                />
                <button
                  onClick={() => onRemove(p.id)}
                  className="absolute top-1 right-1 bg-background/80 hover:bg-background rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`button-remove-photo-${p.id}`}
                >
                  <X className="h-3 w-3" />
                </button>
                <Input
                  value={p.caption || ""}
                  onChange={(e) => onCaption(p.id, e.target.value)}
                  placeholder="Caption (optional)"
                  className="rounded-none border-0 border-t text-xs h-7"
                  data-testid={`input-photo-caption-${p.id}`}
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Step 4: Review + Generate
// ============================================================================

function ReviewStep({
  surveyId,
  data,
  onEditProject,
  onEditRoom,
  onAddMoreRooms,
  onUpdateGeneralNotes,
  flushPendingSave,
}: {
  surveyId: string;
  data: SurveyData;
  onEditProject: () => void;
  onEditRoom: (roomId: string) => void;
  onAddMoreRooms: () => void;
  onUpdateGeneralNotes: (notes: string) => void;
  flushPendingSave: () => Promise<void>;
}) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);

  const rooms = data.rooms || [];
  const project = data.project || {};

  const counts = useMemo(() => {
    let v = 0,
      p = 0,
      w = 0;
    for (const r of rooms) {
      v += r.voiceNotes?.length || 0;
      p += r.photos?.length || 0;
      w += r.workItems?.length || 0;
    }
    return { voice: v, photos: p, work: w };
  }, [rooms]);

  async function handleDownload() {
    setGenerating(true);
    try {
      // Make sure any debounced edits are persisted before we render the PDF
      // from local state — otherwise the downloaded report could lag the DB.
      await flushPendingSave();
      const blob = await generateSiteSurveyPdf({ project, rooms, generalNotes: data.generalNotes });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safe = (project.projectAddress || project.clientName || "site-visit")
        .replace(/[^a-z0-9-_ ]/gi, "")
        .trim()
        .replace(/\s+/g, "-");
      link.download = `Site-Visit-Report-${safe}-${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast({ title: "Could not generate report", description: err.message, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  }

  async function handleSendToAutoQuote() {
    setSending(true);
    try {
      // Flush any pending autosaves first so the survey row reflects the
      // exact state we hand over to Auto Quote.
      await flushPendingSave();
      const blob = await generateSiteSurveyPdf({ project, rooms, generalNotes: data.generalNotes });

      // Upload PDF to object storage
      const fileName = `site-visit-report-${Date.now()}.pdf`;
      const file = new File([blob], fileName, { type: "application/pdf" });

      const presignRes = await fetch("/api/uploads/request-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: fileName, size: file.size, contentType: "application/pdf" }),
      });
      if (!presignRes.ok) throw new Error("Failed to get upload URL");
      const { uploadURL, objectPath } = await presignRes.json();

      const putRes = await fetch(uploadURL, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": "application/pdf" },
      });
      if (!putRes.ok) throw new Error("Failed to upload report PDF");

      // Persist the PDF path on the survey. If this fails we abort and tell
      // the user — otherwise the survey would silently stay as "draft" with
      // no report attached even though Auto Quote opens with the new files.
      const patchRes = await fetch(`/api/site-surveys/${surveyId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "completed", reportObjectPath: objectPath }),
      });
      if (!patchRes.ok) {
        const err = await patchRes.json().catch(() => ({}));
        throw new Error(err.message || "Failed to save survey status");
      }

      // Stash the handover so /quotes/new can pick it up.
      // We pass only the report PDF — it already embeds every room photo at
      // full resolution, so re-attaching them as standalone JPEGs would just
      // make Auto Quote see each image twice (doubling image tokens and
      // risking the model over-weighting photo content vs. the written brief).
      sessionStorage.setItem(
        "site-survey-handover",
        JSON.stringify({
          surveyId,
          reportObjectPath: objectPath,
          reportFileName: fileName,
          project,
        })
      );

      navigate("/quotes/new?from=site-survey");
    } catch (err: any) {
      toast({ title: "Could not send to Auto Quote", description: err.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Project summary */}
      <Card>
        <CardHeader className="flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Project</CardTitle>
            <p className="text-xs text-muted-foreground">
              {project.projectAddress || "(no address)"} · {labelForType(project.projectType)}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onEditProject} data-testid="button-edit-project">
            <Pencil className="h-3 w-3 mr-1" /> Edit
          </Button>
        </CardHeader>
        {project.description && (
          <CardContent>
            <p className="text-sm">{project.description}</p>
          </CardContent>
        )}
      </Card>

      {/* Rooms summary */}
      <Card>
        <CardHeader className="flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Rooms ({rooms.length})</CardTitle>
            <p className="text-xs text-muted-foreground">
              {counts.voice} voice notes · {counts.photos} photos · {counts.work} work items captured
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onAddMoreRooms} data-testid="button-add-more-rooms">
            <Plus className="h-3 w-3 mr-1" /> Add room
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {rooms.map((r) => (
            <button
              key={r.id}
              onClick={() => onEditRoom(r.id)}
              className="w-full text-left rounded-md border p-3 hover:bg-accent/50 transition-colors"
              data-testid={`button-review-room-${r.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm">{r.name}</div>
                <Pencil className="h-3 w-3 text-muted-foreground" />
              </div>
              <div className="text-xs text-muted-foreground mt-1">{roomSummary(r)}</div>
            </button>
          ))}
        </CardContent>
      </Card>

      {/* General notes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">General notes</CardTitle>
          <p className="text-xs text-muted-foreground">Anything that isn't tied to a specific room.</p>
        </CardHeader>
        <CardContent>
          <Textarea
            rows={3}
            value={data.generalNotes || ""}
            onChange={(e) => onUpdateGeneralNotes(e.target.value)}
            placeholder="Site access, parking, neighbour considerations, timeline constraints…"
            data-testid="textarea-general-notes"
          />
        </CardContent>
      </Card>

      {/* Actions */}
      <Card className="border-primary/30 bg-primary/5">
        <CardHeader>
          <CardTitle className="text-base">Generate Site Visit Report</CardTitle>
          <p className="text-xs text-muted-foreground">
            Download the report as a PDF, or send it straight to Auto Quote with all your photos to generate a priced quote.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleDownload}
            disabled={generating || rooms.length === 0}
            data-testid="button-download-report"
          >
            {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
            Download report
          </Button>
          <Button
            onClick={handleSendToAutoQuote}
            disabled={sending || rooms.length === 0}
            data-testid="button-send-to-auto-quote"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Send to Auto Quote
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function labelForType(value?: string): string {
  if (!value) return "no type";
  const t = PROJECT_TYPES.find((p) => p.value === value);
  return t?.label || value;
}
